/** Transcript export through the Document Processor API's tenant connectors. */

import { isArray } from '@ngx-translate/core';

export interface ConnectorCourse {
  term: string;
  startDate: string;
  endDate: string;
  subject: string;
  courseNumber: string;
  courseTitle: string;
  creditsEarned: number;
  grade: string;
  points: number;
  institution: string;
  institutionId: string;
}

/** What both export payloads carry; each adds the array its endpoint expects. */
export interface ConnectorExportBase {
  senderId?: string;
  senderEmailAddress?: string;
  softdocsTranscriptUrl: string;
  /** firstName, middleName, lastName, last4SSN, dateOfBirth, email, studentId. */
  student: Record<string, string>;
}

export interface ConnectorExportRequest extends ConnectorExportBase {
  courses: ConnectorCourse[];
}

/** A high school transcript, which has no courses. */
export interface ConnectorHighSchoolExportRequest extends ConnectorExportBase {
  /** name, ceeb, the GPAs, attendance, graduation, rank and size, as strings. */
  institutions: Record<string, string>[];
}

/** The outcome for one institution's slice of the transcript. */
export interface ConnectorInstitutionResult {
  institutionName: string;
  institutionId: string | null;
  courseCount: number;
  success: boolean;
  message: string | null;
  code: string | null;
}

export interface ConnectorExportResponse {
  configId: string;
  connectorTypeId: number;
  /** succeeded when every institution was accepted, failed when none were, partial in between. */
  status: 'succeeded' | 'partial' | 'failed';
  results: ConnectorInstitutionResult[];
  transcriptId?: string;
}

export interface ConnectorExportRequirements {
  configId: string;
  connectorTypeId: number;
  transcriptTypeId: number;
  requiredStudentFields: string[];
  requiredCourseFields: string[];
  requiredInstitutionFields: string[];
  configuredRules: Record<string, unknown>;
}

export class ConnectorSdk {
  private baseUrl: string;

  /**
   * Constructor
   * @param baseURL the base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseUrl = baseURL;
  }

  /**
   * Reads the API's explanation off a failed response, so a caller sees more than a status.
   * @param response The failed response
   */
  private async readDetail(response: Response): Promise<string> {
    try {
      const body = await response.json();
      if (body.detail) {
        if (isArray(body.detail)) {
          let message = '';
          body.detail.forEach((d: any) => {
            message += `${d.msg} : ${isArray(d.loc) ? d.loc[d.loc.length - 1] : ''}; `;
          });
          return message;
        }
        return body.detail;
      }
      return '';
    } catch {
      return '';
    }
  }

  /**
   * Reads the fields an export of this transcript type will require.
   * @param accessToken The authentication token
   * @param transcriptTypeId 0 for College, 1 for High School
   * @returns The requirements, or null when the tenant has no connector for that type
   */
  async getExportRequirements(
    accessToken: string,
    transcriptTypeId: number
  ): Promise<ConnectorExportRequirements | null> {
    const response = await fetch(
      `${this.baseUrl}/v1/connector/export/transcript-type/${transcriptTypeId}/requirements`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );

    // No connector for this type is an answer, not a failure.
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(
        `Export requirements failed with status: ${response.status}` +
          `${await this.readDetail(response)}`
      );
    }

    return response.json();
  }

  /**
   * Exports a transcripts through the connector configured for each ones type.
   * @param accessToken The authentication token
   * @param payloadItems The transcripts to export: courses for College, institutions for High School
   * @returns The per-institution outcomes; a failed export is a 200 with status failed
   */
  async exportTranscripts(
    accessToken: string,
    payloadItems: (ConnectorExportRequest | ConnectorHighSchoolExportRequest)[]
  ): Promise<ConnectorExportResponse[]> {
    const response = await fetch(`${this.baseUrl}/v1/connector/export-transcripts`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payloadItems)
    });

    if (!response.ok) {
      let errorMessage = `Transcript exports failed with status: ${response.status} `;
      if (response.status == 422) {
        errorMessage += `Transcript(s) failed to parse to a valid format. Please check the following: `;
      }
      errorMessage += `${await this.readDetail(response)}`;

      const error: any = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }

    return response.json();
  }
}
